/**
 * Shared sidebar primitives — used by Sidebar (parent + admin) and
 * TRFerSidebar so every role gets the same brand block, profile row, and
 * footer chrome. (HelperSidebar no longer exists — deleted with the helper-UI
 * removal, #159.)
 *
 * Two important rules baked in:
 *   - The brand chip ALWAYS shows the TRF bird + the Foundation's wordmark —
 *     never the family's coach persona. Its panel icon is the sidebar toggle. The org identity is platform chrome,
 *     not the family's chosen voice character (#872).
 *   - The profile row shows avatar + name + role dot and is the platform's
 *     SINGLE profile entry point (#768). The header's duplicate avatar was
 *     removed in the same change, so this row owns the identity affordance at
 *     every viewport — including mobile, where this sidebar is the drawer.
 *   - The role switcher is its own footer row (`SidebarRoleSwitcher`); each
 *     shell owns its placement — TRFerSidebar keeps it between Settings and
 *     Help, Sidebar (parent + admin) renders it below the profile row (#844).
 *     It used to be a dropdown hanging off the profile row; most accounts have
 *     no second role, so it was invisible machinery attached to the one row
 *     everyone clicks (#768).
 *
 * Sign out is not in this file at all: it is an icon-only control in the
 * header (#768), which supersedes the #626 shape where it lived in a header
 * avatar dropdown. Legal links likewise left `SidebarFooter` for the
 * page-level `LegalStrip`, so the footer is the trf.org link (no tagline
 * since 2026-09-18).
 */
import { forwardRef, type AnchorHTMLAttributes, type ElementType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { roleLabels, unreadWords } from './shell-labels';
import { ROLE_DOT } from '@/lib/tone-tokens';
import { NavLink } from 'react-router-dom';
import { MonoLabel } from '@/components/editorial';
import { useAuth } from '@/contexts/AuthContext';
import { ChevronUp, ExternalLink as ExternalLinkIcon, PanelLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TRFBirdMark, TRFWordmark } from '@/components/brand/TRFLogo';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { User } from '@/types/api';
import { AUTH_POLICY } from '@/lib/auth-policy';

// ── Nav item ──────────────────────────────────────────────────────────────

interface NavItemProps {
  href: string;
  icon: ElementType;
  name: string;
  collapsed: boolean;
  onClose: () => void;
  highlight?: boolean;
  end?: boolean;
  /**
   * Unread items behind this row (ui-ux-guideline §2.7, 2026-09-17). Zero or
   * absent renders nothing. Non-zero: a small numeric chip at the row's end
   * and the row text at 500 (the Slack shape); on the collapsed rail, a dot
   * on the icon with the same words. Chrome, not a status — never a
   * `StatusChip`, never on a page header (§1.6).
   */
  count?: number;
}

// The chip's words, once: the number and "unread" (`shell:sidebar.unread`,
// #2109) — `unreadWords` in `./shell-labels`.

export function UnreadBadge({ count, className }: { count: number; className?: string }) {
  // Subscribes the badge to language changes; the words come from `unreadWords`.
  useTranslation('shell');
  if (!count || count <= 0) return null;
  return (
    <span
      role="img"
      aria-label={unreadWords(count)}
      title={unreadWords(count)}
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-stevens-indigo-solid px-1.5 text-label tabular-nums text-stevens-indigo-solid-fg',
        className,
      )}
    >
      {count}
    </span>
  );
}

export function navLinkClass(isActive: boolean, collapsed: boolean): string {
  return cn(
    // Chrome sits one rung under content (design-grammar §2, card register
    // v3.4, #1713): body-sm at 400, 500 only on the active item, and the
    // 16px glyph the card heads use — at body/500 with 20px icons the
    // sidebar out-weighed the cards it framed.
    'flex items-center rounded-lg px-3 py-2 text-body-sm transition-colors',
    collapsed ? 'justify-center' : 'gap-3',
    isActive
      ? 'bg-stevens-indigo-solid text-stevens-indigo-solid-fg font-medium'
      : 'text-stevens-ink-2 hover:bg-stevens-bg-3 hover:text-stevens-ink',
  );
}

// Collapsed, `NavItem` is a Radix `TooltipTrigger asChild` child: the Slot
// hands it the trigger's ref and pointer/focus handlers, which must land on
// the anchor or the tooltip never opens (#1954 — it did not, on hover or
// focus, while `NavItem` took only its own props).
type NavItemElementProps = NavItemProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NavItemProps | 'className' | 'children'>;

const NavItem = forwardRef<HTMLAnchorElement, NavItemElementProps>(function NavItem(
  { href, icon: Icon, name, collapsed, onClose, highlight, end, count = 0, onClick, ...triggerProps },
  ref,
) {
  useTranslation('shell');
  const unread = count > 0;
  return (
    <NavLink
      ref={ref}
      {...triggerProps}
      // The tooltip repeats the name, so an open tooltip's aria-describedby
      // would announce the label twice; the sr-only label below is the name.
      aria-describedby={undefined}
      to={href}
      end={end}
      onClick={(e) => {
        onClick?.(e);
        onClose();
      }}
      className={({ isActive }) =>
        cn(
          navLinkClass(isActive, collapsed),
          highlight && 'ring-2 ring-stevens-indigo ring-offset-2 animate-pulse',
        )
      }
    >
      {/* Collapsed, the label is screen-reader text and leads the link, so
          the name is "<label>" or "<label> <n> unread" — the same words the
          expanded row reads. The tooltip cannot carry it: it mounts only on
          hover or focus and links by aria-describedby, a description, not a
          name (#1954). An sr-only span, not aria-label, so the dot's words
          still append. */}
      {collapsed && <span className="sr-only">{name}</span>}
      <span className="relative inline-flex flex-shrink-0">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {collapsed && unread && (
          <span
            role="img"
            aria-label={unreadWords(count)}
            title={unreadWords(count)}
            className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-stevens-indigo-solid ring-2 ring-stevens-bg-2"
          />
        )}
      </span>
      {!collapsed && <span className={cn('min-w-0 flex-1 line-clamp-2 break-words', unread && 'font-medium')}>{name}</span>}
      {!collapsed && unread && <UnreadBadge count={count} className="ml-auto flex-shrink-0" />}
    </NavLink>
  );
});

export function NavItemWithTooltip(props: NavItemProps) {
  return props.collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavItem {...props} />
      </TooltipTrigger>
      <TooltipContent side="right">{props.name}</TooltipContent>
    </Tooltip>
  ) : (
    <NavItem {...props} />
  );
}

// ── Brand block (top of sidebar) ──────────────────────────────────────────
//
// Always the TRF mark + org name, not the family's coach persona. The brand
// is platform identity — fixed across roles (#872).
//
// 2026-09-20 (Victor): the block is `[panel icon] [bird] TYLER ROBINSON /
// FOUNDATION` — the Foundation's own logo, bird and outlined wordmark, from
// `components/brand/TRFLogo`. The panel icon is the sidebar's one control:
// on md+ it collapses and expands the pinned rail (it was the header
// hamburger, #1725); below md, and in hamburger mode, it closes the drawer.
// Collapsed, the block stacks the icon over the bird.

interface SidebarBrandProps {
  collapsed: boolean;
  /** True when the sidebar is a pinned rail on md+; false for the drawer. */
  pinned?: boolean;
  /** Collapse / expand the pinned rail (md+ only). */
  onToggle?: () => void;
  /** Close the drawer (below md, or hamburger mode). */
  onClose?: () => void;
}

const BRAND_BUTTON_CLASS = 'h-9 w-9 flex-shrink-0 text-stevens-ink-2 hover:text-stevens-ink hover:bg-stevens-bg-3';

export function SidebarBrand({ collapsed, pinned = true, onToggle, onClose }: SidebarBrandProps) {
  const { t } = useTranslation('shell');
  return (
    <div
      className={cn(
        'flex border-b border-border/60',
        collapsed ? 'flex-col items-center gap-1 px-2 pt-3 pb-3' : 'items-center gap-2 px-3 pt-3 pb-3',
      )}
    >
      {/* Drawer close — the only control below md; the only one at all when
          the sidebar is never a pinned rail (hamburger mode). */}
      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(BRAND_BUTTON_CLASS, pinned && 'md:hidden')}
          onClick={onClose}
          aria-label={t('sidebar.closeMenu')}
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      )}
      {/* Rail toggle — md+ on a pinned rail. */}
      {pinned && onToggle && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(BRAND_BUTTON_CLASS, 'hidden md:inline-flex')}
          onClick={onToggle}
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-pressed={collapsed}
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      )}
      <span className="flex-shrink-0 text-stevens-indigo" aria-hidden>
        <TRFBirdMark height={28} />
      </span>
      {!collapsed && (
        <span className="min-w-0 flex items-center text-foreground">
          <TRFWordmark height={22} className="max-w-full" />
        </span>
      )}
      {/* The Foundation's legal name is a proper noun: never a catalog value. */}
      <span className="sr-only" translate="no">Tyler Robinson Foundation</span>
    </div>
  );
}

// The `CollapseToggle` row that sat under the brand block was deleted 2026-09-05
// (#1725): the header hamburger is the rail toggle on md+ now.

// ── Profile row (bottom of sidebar) ───────────────────────────────────────

// Dots come from the unified role map (#762): the parent dot wore ok-green
// (a status hue on an identity mark) and applicant wore brand indigo — both
// now the categorical family that matches the role's chip tone everywhere.
//
// `label` is a getter over `shell:roles.*` (#2109, `./shell-labels`),
// resolved when READ, so a language change reaches every consumer without a
// second map — the same shape as `navItem` in `src/i18n/keys/nav.ts`. `TRFer` is a glossary
// `doNotTranslate` term; its catalog value stays `TRFer` in every locale.
export const ROLE_DISPLAY: Record<User['role'], { label: string; dot: string }> = {
  admin:     { get label() { return roleLabels.admin(); },     dot: ROLE_DOT.admin },
  trfer:     { get label() { return roleLabels.trfer(); },     dot: ROLE_DOT.trfer },
  parent:    { get label() { return roleLabels.parent(); },    dot: ROLE_DOT.parent },
  applicant: { get label() { return roleLabels.applicant(); }, dot: ROLE_DOT.applicant },
};

interface SidebarProfileRowProps {
  collapsed: boolean;
  /**
   * Opens the profile dialog. Threaded from `MainLayout`, which owns the
   * dialog state and hands the same callback to the header.
   *
   * Optional so the row degrades to a static identity display rather than a
   * dead button if a shell forgets to pass it — a click that does nothing is
   * worse than a row that never invited the click.
   */
  onProfileClick?: () => void;
}

export function SidebarProfileRow({ collapsed, onProfileClick }: SidebarProfileRowProps) {
  const { user } = useAuth();
  const { t } = useTranslation('shell');
  if (!user) return null;

  const firstName = user.profile?.first_name ?? '';
  const lastInitial = user.profile?.last_name?.charAt(0) ?? '';
  const displayName = firstName ? `${firstName}${lastInitial ? ` ${lastInitial}.` : ''}` : (user.email ?? t('profileRow.account'));
  const initials =
    firstName && lastInitial
      ? `${firstName.charAt(0)}${lastInitial}`.toUpperCase()
      : firstName.slice(0, 2).toUpperCase() || (user.email ?? '??').slice(0, 2).toUpperCase();
  // Fall back for roles missing from ROLE_DISPLAY (e.g. legacy `helper`) so an
  // unmapped role can never crash the whole layout (B-119).
  const role = ROLE_DISPLAY[user.role] ?? { label: user.role, dot: 'bg-muted-foreground' };

  const rowContent = (
    <>
      <span translate="no" className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-stevens-indigo-solid text-label font-semibold text-stevens-indigo-solid-fg">
        {initials}
      </span>
      {!collapsed && (
        <>
          <div className="flex-1 min-w-0 text-left">
            {/* A person's name is data, not copy. */}
            <div className="text-body-sm font-medium text-foreground truncate" translate="no">{displayName}</div>
            <div className="flex items-center gap-1 text-label text-stevens-ink-3">
              <span className={cn('w-[6px] h-[6px] rounded-full flex-shrink-0', role.dot)} />
              <span>{role.label}</span>
              <span aria-hidden className="text-stevens-ink-3/40">·</span>
              <span>{t('profileRow.active')}</span>
            </div>
          </div>
        </>
      )}
    </>
  );

  const rowClass = cn(
    'w-full flex items-center rounded-lg px-2 py-1.5',
    collapsed ? 'justify-center' : 'gap-2.5',
  );

  // No handler → a static identity row. A row that looks clickable and does
  // nothing is a worse failure than one that never invited the click.
  if (!onProfileClick) {
    const staticRow = (
      <div className={rowClass} aria-label={`${displayName} · ${role.label}`}>
        {rowContent}
      </div>
    );
    return collapsed ? (
      <Tooltip>
        <TooltipTrigger asChild>{staticRow}</TooltipTrigger>
        <TooltipContent side="right">
          {displayName} · {role.label}
        </TooltipContent>
      </Tooltip>
    ) : (
      staticRow
    );
  }

  const trigger = (
    <button
      type="button"
      onClick={onProfileClick}
      className={cn(rowClass, 'group hover:bg-muted transition-colors')}
      aria-label={t('profileRow.open', { name: displayName, role: role.label })}
    >
      {rowContent}
    </button>
  );

  return collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right">
        {displayName} · {role.label}
      </TooltipContent>
    </Tooltip>
  ) : (
    trigger
  );
}

// ── Role switcher (footer row — each sidebar shell owns its placement) ────

interface SidebarRoleSwitcherProps {
  collapsed: boolean;
}

/**
 * Role switching, as its own unobtrusive footer row (#768).
 *
 * Renders NOTHING unless the account actually has more than one enabled role,
 * which is the common case — most staff have exactly one. That is the whole
 * reason it moved out of the profile row: it was invisible machinery bolted to
 * the row every user clicks, so the row's affordance changed depending on a
 * property of the account the user could not see.
 *
 * No avatar or logo in the trigger, per #768 — the identity is already stated
 * one row below, and repeating it here made the footer read as two accounts.
 */
export function SidebarRoleSwitcher({ collapsed }: SidebarRoleSwitcherProps) {
  const { user, switchRole, canSwitchRole } = useAuth();
  const { t } = useTranslation('shell');
  if (!user) return null;

  const switchableRoles = canSwitchRole
    ? (user.roles ?? []).filter((r) => !AUTH_POLICY.disabledRoles.includes(r))
    : [];
  if (switchableRoles.length <= 1) return null;

  const role = ROLE_DISPLAY[user.role] ?? { label: user.role, dot: 'bg-muted-foreground' };

  const trigger = (
    <button
      type="button"
      className={cn(
        'w-full flex items-center rounded-lg px-2 py-1.5 hover:bg-muted transition-colors',
        collapsed ? 'justify-center' : 'gap-2.5',
      )}
      aria-label={t('roleSwitcher.aria', { role: role.label })}
    >
      <span className={cn('w-[6px] h-[6px] rounded-full flex-shrink-0', role.dot)} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left text-body-sm text-stevens-ink-2 truncate">
            {t('roleSwitcher.label')}
          </span>
          <ChevronUp className="h-3.5 w-3.5 text-stevens-ink-3 flex-shrink-0" />
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{t('roleSwitcher.tooltip', { role: role.label })}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      {/* z-[70]: the switcher also sits in the z-[60] drawer (#2435), and the
          menu is portaled to <body>; at the default z-50 it opened behind the
          drawer and its scrim. tailwind-merge drops the default z-50. */}
      <DropdownMenuContent align="start" side="top" className="w-56 z-[70]">
        <MonoLabel className="block px-2 py-1">{t('roleSwitcher.label')}</MonoLabel>
        {switchableRoles.map((r) => {
          const cfg = ROLE_DISPLAY[r] ?? { label: r.charAt(0).toUpperCase() + r.slice(1), dot: 'bg-muted-foreground' };
          const isActive = r === user.role;
          return (
            <DropdownMenuItem
              key={r}
              onClick={() => { if (!isActive) void switchRole(r); }}
              className={cn('gap-2 cursor-pointer', isActive && 'font-medium')}
            >
              <span className={cn('w-[6px] h-[6px] rounded-full', cfg.dot)} />
              <span>{cfg.label}</span>
              {isActive && <span className="ml-auto text-label text-stevens-ink-3">{t('roleSwitcher.active')}</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Footer tagline ────────────────────────────────────────────────────────

interface SidebarFooterProps {
  collapsed: boolean;
  /**
   * Optional since 2026-09-18: no shell passes one any more (ui-ux-guideline
   * §2.8 rule 6 — a sentence in the chrome is deleted), and the footer is
   * the trf.org link alone when it is absent.
   */
  tagline?: ReactNode;
}

/**
 * A small trf.org attribution link, with an optional tagline above it. The
 * Privacy · Terms · SMS · Cookies · All legal strip moved out to the
 * page-level `LegalStrip` (#768) so it stays visible while the sidebar
 * scrolls — in a long nav the links used to be pushed below the fold, which
 * is the one place a legal link must never be. The trf.org link replaces the
 * attribution the fixed TRFBanner used to carry on every page (#879) —
 * hidden when the sidebar is collapsed.
 */
export function SidebarFooter({ collapsed, tagline }: SidebarFooterProps) {
  if (collapsed) return null;
  return (
    <div className="px-4 pb-3 -mt-1 space-y-1.5">
      {tagline && (
        <p className="font-serif text-label italic text-stevens-ink-3/80 leading-snug">
          {tagline}
        </p>
      )}
      <ExternalLink
        href="https://www.trf.org"
        className="inline-flex items-center gap-1 text-stevens-ink-3/80 hover:text-stevens-ink-2 transition-colors"
      >
        {/* A domain, not copy. */}
        <MonoLabel className="font-normal" translate="no">trf.org</MonoLabel>
        <ExternalLinkIcon className="h-2.5 w-2.5" aria-hidden />
      </ExternalLink>
    </div>
  );
}

// ── Section group helpers ────────────────────────────────────────────────

export interface NavItemDef {
  name: string;
  href: string;
  icon: ElementType;
  end?: boolean;
}

export interface NavSection {
  /** Mono-uppercase label shown above the group on desktop. Omit for utility tray. */
  label?: string;
  items: NavItemDef[];
}
